import { observer } from 'mobx-react';
import React from 'react';
import { Button } from '@components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/ui/card';
import { cn } from '@core/utils/cn';
import { UserContext, UserTypeContext } from '..';
import { useError } from '../../../components/hooks/useError';
import { Api } from '../../../core/api';
import type { UpdateUserTypeSupportDto } from '../../../core/api/MobileClient';
import { injectTSDI } from '../../../core/tsdi';

type UserType = UpdateUserTypeSupportDto['userType'];

const USER_TYPES: UserType[] = ['USER', 'COACH', 'SUPPORT'];

const HELP: Record<UserType, string> = {
    USER: 'Regular member. Logs moves, joins challenges, posts text and photos in community feeds.',
    COACH: 'Everything a user can do, plus video attachments in community feed posts.',
    SUPPORT: 'Internal Move Republic staff account. Same app permissions as a coach.'
};

export const UserTypeCard = observer(() => {
    const user = React.useContext(UserContext);
    const { userType, setUserType } = React.useContext(UserTypeContext);
    const { mobileClient } = injectTSDI(Api);
    const showError = useError();
    const [pendingType, setPendingType] = React.useState<UserType>();
    const [saving, setSaving] = React.useState(false);

    async function confirmChange() {
        if (!pendingType) {
            return;
        }
        const previous = userType;
        setUserType(pendingType);
        setPendingType(undefined);
        setSaving(true);
        try {
            const result =
                await mobileClient.supportApi.userSearchSupportControllerUpdateUserType(
                    user.id,
                    { userType: pendingType }
                );
            setUserType(result.data.userType);
        } catch (e) {
            setUserType(previous);
            showError(e);
        } finally {
            setSaving(false);
        }
    }

    return (
        <Card>
            <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-6">
                <div className="flex max-w-lg flex-col gap-1">
                    <CardTitle>User type</CardTitle>
                    <CardDescription>{HELP[userType]}</CardDescription>
                </div>
                <div role="group" className="inline-flex">
                    {USER_TYPES.map((type, index) => {
                        const active = type === userType;
                        return (
                            <Button
                                key={type}
                                type="button"
                                variant="outline"
                                size="sm"
                                aria-pressed={active}
                                disabled={saving}
                                onClick={() =>
                                    setPendingType(active ? undefined : type)
                                }
                                className={cn(
                                    'rounded-none tracking-wide',
                                    index === 0 && 'rounded-l-md',
                                    index === USER_TYPES.length - 1 && 'rounded-r-md',
                                    index > 0 && '-ml-px',
                                    active && 'bg-primary/15 text-primary',
                                    type === pendingType && 'bg-accent'
                                )}
                            >
                                {type}
                            </Button>
                        );
                    })}
                </div>
            </CardHeader>
            {pendingType && (
                <CardContent>
                    <div className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-primary bg-primary/5 p-4">
                        <div className="flex flex-col gap-1">
                            <span className="font-medium">
                                Change {user.firstName} from {userType} to {pendingType}?
                            </span>
                            <span className="text-sm text-muted-foreground">
                                {HELP[pendingType]} Applies immediately and is not recorded in any history.
                            </span>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setPendingType(undefined)}
                            >
                                Keep {userType}
                            </Button>
                            <Button onClick={() => void confirmChange()}>
                                Change to {pendingType}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            )}
        </Card>
    );
});
